import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { getMaterials } from "../../adminPrs/redux/adminPrsSlice";
import { projectToItemRequisition } from "../redux/projectSlice";
import {
  CustomTableContainer,
  TableHeaderFont,
} from "../../../styles/tableContent.style";
import { CustomButton } from "../../vendorRegistration/components/StepMain";
import moment from "moment";
import { styled } from "@mui/material/styles";

const ProjectItemRequisitionModal = ({ open, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const { materials } = useSelector((state) => state.adminPrs);
  const { projectDetails, itemRequisitionLoading } = useSelector(
    (state) => state.project,
  );

  const [items, setItems] = useState([]);

  useEffect(() => {
    if (open) {
      setItems([
        {
          material_masters_id: "",
          quantity: "",
          expected_start_date: null,
          expected_completion_date: null,
          recommended_sourcing_strategy: "",
          target_contracts: "",
          justification: "",
          suggested_supplier: "",
          budget_owner_name: "",
          budget: "",
          selectedMaterial: null,
        },
      ]);
    }
  }, [open]);

  const handleAddItem = () => {
    setItems([
      ...items,
      {
        material_masters_id: "",
        quantity: "",
        expected_start_date: null,
        expected_completion_date: null,
        recommended_sourcing_strategy: "",
        target_contracts: "",
        justification: "",
        suggested_supplier: "",
        budget_owner_name: "",
        budget: "",
        selectedMaterial: null,
      },
    ]);
  };

  const handleRemoveItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems.length ? newItems : []);
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    if (field === "selectedMaterial") {
      newItems[index].material_masters_id = value ? value.id : "";
      // Pre-fill description if needed, or just keep it in selectedMaterial
    }
    setItems(newItems);
  };

  const handleMaterialSearch = (value) => {
    if (value.length >= 3) {
      dispatch(getMaterials({ search_by: "item_name", search_key: value }));
    }
  };

  const handleSubmit = async () => {
    // Validate
    const isValid = items.every(
      (item) =>
        item.material_masters_id &&
        item.quantity &&
        item.expected_start_date &&
        item.expected_completion_date,
    );

    if (!isValid) {
      // Allow empty submission? No.
      alert("Please fill all required fields for all items.");
      return;
    }

    const payload = {
      project_id: id,
      business_unit_id: projectDetails?.business_unit_id,
      items: items.map((item) => ({
        material_masters_id: item.material_masters_id,
        quantity: Number(item.quantity),
        description:
          item.selectedMaterial?.LongDescription ||
          item.selectedMaterial?.Description ||
          "",
        expected_start_date: item.expected_start_date
          ? moment(item.expected_start_date).format("YYYY-MM-DD")
          : null,
        expected_completion_date: item.expected_completion_date
          ? moment(item.expected_completion_date).format("YYYY-MM-DD")
          : null,
        recommended_sourcing_strategy: item.recommended_sourcing_strategy,
        target_contracts: item.target_contracts,
        justification: item.justification,
        suggested_supplier: item.suggested_supplier,
        budget_owner_id: item.budget_owner_name,
        budget: item.budget ? Number(item.budget) : null,
      })),
    };

    const result = await dispatch(projectToItemRequisition(payload));
    if (result.type.endsWith("fulfilled")) {
      // Correct check for success should rely on payload.details or success flag if returned
      // The slice handles notifications.
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          Create Item Requisition
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <CustomTableContainer component={Paper}>
          <Table size="small" aria-label="simple table">
            <TableHead>
              <TableRow
                style={{
                  backgroundColor: "#e1ebfa",
                }}
              >
                <TableCell style={{ minWidth: "350px" }}>
                  <TableHeaderFont>Item Name</TableHeaderFont>
                </TableCell>
                <TableCell style={{ minWidth: "180px" }}>
                  <TableHeaderFont>Item Code</TableHeaderFont>
                </TableCell>
                <TableCell style={{ minWidth: "120px" }}>
                  <TableHeaderFont>Qty</TableHeaderFont>
                </TableCell>
                <TableCell style={{ minWidth: "130px" }}>
                  <TableHeaderFont>Start Date</TableHeaderFont>
                </TableCell>
                <TableCell style={{ minWidth: "130px" }}>
                  <TableHeaderFont>End Date</TableHeaderFont>
                </TableCell>
                <TableCell style={{ minWidth: "150px" }}>
                  <TableHeaderFont>Sourcing Strategy</TableHeaderFont>
                </TableCell>
                <TableCell style={{ minWidth: "150px" }}>
                  <TableHeaderFont>Target Contracts</TableHeaderFont>
                </TableCell>
                <TableCell style={{ minWidth: "200px" }}>
                  <TableHeaderFont>Justification</TableHeaderFont>
                </TableCell>
                <TableCell style={{ minWidth: "150px" }}>
                  <TableHeaderFont>Supplier</TableHeaderFont>
                </TableCell>
                <TableCell style={{ minWidth: "150px" }}>
                  <TableHeaderFont>Budget Owner Name</TableHeaderFont>
                </TableCell>
                <TableCell style={{ minWidth: "120px" }}>
                  <TableHeaderFont>Budget</TableHeaderFont>
                </TableCell>
                <TableCell style={{ minWidth: "100px" }}>
                  <TableHeaderFont>Action</TableHeaderFont>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, index) => (
                <TableRow
                  key={index}
                  style={{
                    backgroundColor: "#fafcff",
                  }}
                >
                  <TableCell>
                    <Autocomplete
                      options={materials || []}
                      getOptionLabel={(option) =>
                        option.Description || option.LongDescription || ""
                      }
                      onInputChange={(_, value) => handleMaterialSearch(value)}
                      onChange={(_, value) =>
                        updateItem(index, "selectedMaterial", value)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="standard"
                          placeholder="Search Item"
                        />
                      )}
                      value={item.selectedMaterial}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Autocomplete
                      options={materials || []}
                      getOptionLabel={(option) => option.MaterialCode || ""}
                      onInputChange={(_, value) => {
                        if (value.length >= 3) {
                          dispatch(
                            getMaterials({
                              search_by: "item_code",
                              search_key: value,
                            }),
                          );
                        }
                      }}
                      onChange={(_, value) =>
                        updateItem(index, "selectedMaterial", value)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="standard"
                          placeholder="Search Code"
                        />
                      )}
                      value={item.selectedMaterial}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      variant="standard"
                      value={item.quantity}
                      onChange={(e) =>
                        updateItem(index, "quantity", e.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="date"
                      variant="standard"
                      InputLabelProps={{ shrink: true }}
                      value={
                        item.expected_start_date
                          ? moment(item.expected_start_date).format(
                              "YYYY-MM-DD",
                            )
                          : ""
                      }
                      onChange={(e) =>
                        updateItem(
                          index,
                          "expected_start_date",
                          e.target.value ? moment(e.target.value) : null,
                        )
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="date"
                      variant="standard"
                      InputLabelProps={{ shrink: true }}
                      value={
                        item.expected_completion_date
                          ? moment(item.expected_completion_date).format(
                              "YYYY-MM-DD",
                            )
                          : ""
                      }
                      onChange={(e) =>
                        updateItem(
                          index,
                          "expected_completion_date",
                          e.target.value ? moment(e.target.value) : null,
                        )
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      variant="standard"
                      value={item.recommended_sourcing_strategy}
                      onChange={(e) =>
                        updateItem(
                          index,
                          "recommended_sourcing_strategy",
                          e.target.value,
                        )
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      variant="standard"
                      value={item.target_contracts}
                      onChange={(e) =>
                        updateItem(index, "target_contracts", e.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      variant="standard"
                      value={item.justification}
                      onChange={(e) =>
                        updateItem(index, "justification", e.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      variant="standard"
                      value={item.suggested_supplier}
                      onChange={(e) =>
                        updateItem(index, "suggested_supplier", e.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      variant="standard"
                      value={item.budget_owner_name}
                      onChange={(e) =>
                        updateItem(index, "budget_owner_name", e.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      variant="standard"
                      value={item.budget}
                      onChange={(e) =>
                        updateItem(index, "budget", e.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <CustomButton
                      className="invoiceErrbutn"
                      color="error"
                      variant="outlined"
                      size="small"
                      onClick={() => handleRemoveItem(index)}
                      style={{
                        marginLeft: "3px ",
                        textAlign: "center",
                      }}
                    >
                      Remove
                    </CustomButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CustomTableContainer>
        <Box mt={2}>
          <CustomButton
            startIcon={<AddIcon />}
            variant="outlined"
            onClick={handleAddItem}
          >
            Add Item
          </CustomButton>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={itemRequisitionLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={itemRequisitionLoading}
        >
          {itemRequisitionLoading ? "Submitting..." : "Submit Requisition"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectItemRequisitionModal;
